import { Controller, useForm } from 'react-hook-form';
import { RichTextEditor } from '@/components/forms/RichTextEditor';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/components/dialogs/Modal';
import { TextField } from '@/components/forms/TextField';
import { MAX_ANSWER_LENGTH, MAX_QUESTION_LENGTH } from '@/api/types/faqHost';

export interface FaqFormValues {
  question: string;
  answer: string;
}

const schema = z.object({
  question: z
    .string()
    .trim()
    .min(1, 'Please enter a question.')
    .max(MAX_QUESTION_LENGTH, `Question must be at most ${MAX_QUESTION_LENGTH} characters.`),
  answer: z
    .string()
    .trim()
    .min(1, 'Please enter an answer.')
    .max(MAX_ANSWER_LENGTH, `Answer must be at most ${MAX_ANSWER_LENGTH} characters.`),
});

interface FaqFormModalProps {
  show: boolean;
  mode: 'create' | 'edit';
  initial?: { question: string; answer: string };
  busy?: boolean;
  onSubmit: (values: FaqFormValues) => void;
  onClose: () => void;
}

// Legacy used a Quill rich-text editor for the answer (HTML stored). Pending a
// shared React rich-text component, the answer is edited as raw HTML/text in a
// textarea; the stored value remains HTML so existing answers render unchanged.
export function FaqFormModal({
  show,
  mode,
  initial,
  busy = false,
  onSubmit,
  onClose,
}: FaqFormModalProps) {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FaqFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { question: initial?.question ?? '', answer: initial?.answer ?? '' },
  });

  return (
    <Modal
      show={show}
      title={mode === 'create' ? 'Add FAQ' : 'Edit FAQ'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
            Close
          </button>
          <button type="submit" form="faq-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="faq-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          label="Question"
          maxLength={MAX_QUESTION_LENGTH}
          error={errors.question?.message}
          {...register('question')}
        />
        <div className="mb-3">
          <label htmlFor="faq-answer" className="form-label">
            Answer
          </label>
          <Controller
            control={control}
            name="answer"
            render={({ field }) => <RichTextEditor value={field.value ?? ''} onChange={field.onChange} />}
          />
          {errors.answer && <div className="text-danger small mt-1">{errors.answer.message}</div>}
        </div>
      </form>
    </Modal>
  );
}
